import * as React from 'react';
import { ApiHelper } from '../../ApiHelper';
import { useConfig } from '../../configContext';
import { Service } from '../../interfaces/Service';
import { ProfileDay } from './types';

export function useDocumentTitle(title?: string | null) {
    React.useEffect(() => {
        if (!title) {
            return;
        }

        const previousTitle = window.document.title;
        window.document.title = `PR Guardian - ${title}`;

        return () => {
            window.document.title = previousTitle;
        };
    }, [title]);
}

export function useCurrentService(serviceName?: string) {
    const config = useConfig();

    return React.useMemo(() => {
        if (!serviceName) {
            return undefined;
        }

        return config?.services?.find((service: Service & { Name?: string }) => (service.name ?? service.Name) === serviceName);
    }, [config, serviceName]);
}

export function useProfileDates(
    serviceName: string | undefined,
    enabled: boolean,
    environmentId?: number,
    processId?: number,
    metricId?: number,
) {
    const [dates, setDates] = React.useState<ProfileDay[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!enabled || !serviceName || environmentId === undefined || processId === undefined || metricId === undefined) {
            setDates([]);
            setError(null);
            setLoading(false);
            return;
        }

        let isMounted = true;
        setLoading(true);
        setError(null);

        ApiHelper(`/api/v3/service/${serviceName}/aggregatedprofiles/${environmentId}/${processId}/${metricId}`)
            .then((response: ProfileDay[]) => {
                if (!isMounted) {
                    return;
                }

                setDates(Array.isArray(response) ? response : []);
                setLoading(false);
            })
            .catch((err: Error) => {
                if (!isMounted) {
                    return;
                }

                setError(err.message || 'Failed to load available profiles.');
                setDates([]);
                setLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [enabled, environmentId, metricId, processId, serviceName]);

    return { dates, loading, error };
}

